const ExcludeNonFocusableElements =
    ':not([disabled]):not(.-hidden):not([type="hidden"]):not([tabindex="-1"])';
/**
 * All possible HTML5 boolean attributes.
 * @memberof UiHelpers
 * @type {Array<string>}
 */
export const BooleanAttributes = [
    'allowfullscreen',
    'async',
    'autofocus',
    'autoplay',
    'checked',
    'controls',
    'default',
    'defer',
    'disabled',
    'formnovalidate',
    'inert',
    'ismap',
    'itemscope',
    'loop',
    'multiple',
    'muted',
    'nomodule',
    'novalidate',
    'open',
    'playsinline',
    'readonly',
    'required',
    'reversed',
    'selected',
    'shadowrootclonable',
    'shadowrootdelegatesfocus',
    'shadowrootserializable',
];

/**
 * List of primary interactive elements. Usually used to determine
 * focusable elements.
 * @memberof UiHelpers
 * @type {Array<string>}
 */
export const PrimaryInteractiveElements = [
    'input',
    'select',
    'textarea',
    'summary',
    '[tabindex]',
].map((selector) => `${selector}${ExcludeNonFocusableElements}`);

/**
 * List of secondary interactive elements. Usually used to determine
 * focusable elements.
 * @memberof UiHelpers
 * @type {Array<string>}
 */
export const SecondaryInteractiveElements = ['a', 'button'].map(
    (selector) => `${selector}${ExcludeNonFocusableElements}`
);

/**
 * SVG - related tags
 * @memberof UiHelpers
 * @type {Array<string>}
 */
export const SVGElements = [
    'svg',
    'use',
    'path',
    'g',
    'defs',
    'filter',
    'feComponentTransfer',
    'feFuncR',
    'feFuncG',
    'feFuncB',
];

/**
 * Input elements.
 * @memberof UiHelpers
 * @type {Array}
 */
export const NativeFormElements = [
    'input:not([type=radio]):not([type=checkbox])',
    'select',
    'textarea',
    'output',
    'summary',
];

export const CustomVirtualFormElements = [
    'ui-dropdown',
    'ui-slider',
    'ui-monthpicker',
    'ui-monthrange',
    'ui-daterange',
    'ui-timerange',
    'ui-datepicker',
    'ui-upload',
    'ui-stepper',
];

/**
 * Custom input elements.
 * @memberof UiHelpers
 * @type {Array}
 */
export const CustomFormElements = [
    'ui-autocomplete',
    'ui-textcounter',
    'ui-searchinput',
    'ui-timepicker',
    ...CustomVirtualFormElements,
];

/**
 * @type {PopoverAlignment}
 */
export const PopoverAlignment = {
    Top: 'top',
    Middle: 'middle',
    Bottom: 'bottom',
    Left: 'left',
    Right: 'right',
    None: 'none',
};

/**
 * @type {Pick<PopoverAlignment, "Left" | "Right" | "None">}
 */
export const PopoverShifting = {
    Left: PopoverAlignment.Left,
    Right: PopoverAlignment.Right,
    None: PopoverAlignment.None,
};

/**
 * @type {string}
 */
export const MenuOverlayCSSClass = '-ui-menu-overlay';

/**
 * @type {string}
 */
export const TopModalCSSClass = '-ui-top-modal';

export const TimeInSeconds: Record<string, number> = {
    Second: 1,
    Minute: 60,
    Hour: 3600,
    Day: 86400,
    Week: 604800,
} as const;

/**
 * @typedef {("top" | "middle" | "bottom" | "left" | "right" | "none")} AlignmentValue
 */
/**
 * @typedef {object} PopoverAlignment
 * @property {Extract<AlignmentValue, "top">} Top - top alignment.
 * @property {Extract<AlignmentValue, "middle">} Middle - middle alignment.
 * @property {Extract<AlignmentValue, "bottom">} Bottom - bottom alignment.
 * @property {Extract<AlignmentValue, "left">} Left - left alignment.
 * @property {Extract<AlignmentValue, "right">} Right - right alignment.
 * @property {Extract<AlignmentValue, "none">} None - no alignment, use if you know what
 * you are doing.
 */
